// Nothing is a one-way trip (UX-13, PLAN-2026-10-17 §2.4, DIRECTION U24).
//
// Every screen that is not a place's first has a Back of its own in the top
// bar, at every width, installed or not: an arrow with the name of where it
// goes, as an app's navigation bar has. Returning used to depend on the
// browser's Back or the phone's, and an installed app on a desktop or an
// iPhone has neither; the rail and the tabs land on a place's first screen,
// not where the person was.
//
// WHERE IT GOES. To where the person came from — the screen before this one
// in this app's own history — and, when they arrived from outside it (a
// notification, a link, a fresh load) or by the tabs or the rail, to the
// screen this one belongs to (`parent`). A tab or the rail is a jump between
// places, and Back after one goes up rather than sideways to the place left,
// as a tab bar's stack does.
//
// THE HISTORY IS READ, NOT KEPT TWICE. BrowserRouter numbers each entry
// (`idx` in its state); this keeps what each number held — its address,
// whether a jump made it, and the name a screen gave itself — so the entry
// before can be named before it is gone back to. Entries at the same address
// (a sheet's own, B-308; a week stepped through in the query) are one screen,
// and Back passes all of them at once. Kept for the tab's life in session
// storage, as the history itself is, so a reload keeps its way back.

import { useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { tabMoves } from './screenMotion';
import { morph } from './morph';

/** Somewhere to go back to, by name. */
export type Way = { to: string; label: string };

/** What a screen is called, and the screen it belongs to (null: a place's first). */
type Screen = { name: string; parent: Way | null };

/**
 * A league screen by the path after `/l/:id`. `setup` is a league that has not
 * drafted, whose own front is the pre-draft page rather than My Team.
 */
function leagueScreen(base: string, rest: string, search: string, setup: boolean): Screen | null {
  const up = (path: string, label: string): Way => ({ to: `${base}${path}`, label });
  const front = setup ? up('', 'League') : up('/league', 'League');
  const team = up('', setup ? 'League' : 'My Team');
  const tab = new URLSearchParams(search);
  if (rest === '' || rest === '/' || rest === '/lineup') return { name: team.label, parent: null };
  if (rest === '/matchup') return { name: 'Game Day', parent: null };
  if (rest === '/players') return { name: 'Players', parent: null };
  if (rest === '/league') return { name: 'League', parent: null };
  if (rest === '/calendar') return { name: 'Calendar', parent: null };
  if (rest.startsWith('/matchup/')) return { name: 'Matchup', parent: up('/matchup', 'Game Day') };
  if (rest.startsWith('/teams/')) return { name: 'Team', parent: front };
  // The draft is a tab before it happens and a record after.
  if (rest === '/draft') return { name: 'Draft room', parent: setup ? null : front };
  if (rest.startsWith('/draft/mock') || rest === '/draft/together')
    return {
      name: rest === '/draft/together' ? 'League mock' : 'Mock draft',
      parent: up('/draft', 'Draft room'),
    };
  if (rest === '/priorities') return { name: 'Draft plan', parent: up('/draft', 'Draft room') };
  // The finished draft played back (C11) belongs to the board it plays.
  if (rest === '/draft/replay') return { name: 'Draft replay', parent: up('/draft', 'Draft room') };
  // The draft on a TV (M3) is the room's own view of itself.
  if (rest === '/draft/tv') return { name: 'Draft on a TV', parent: up('/draft', 'Draft room') };
  // My Team's tools (B-316): they belong to the roster they work on.
  if (rest === '/waivers')
    return tab.get('show') === 'log'
      ? { name: 'League activity', parent: front }
      : { name: 'Waivers', parent: team };
  if (rest === '/trades') return { name: 'Trades', parent: team };
  if (rest === '/plan') return { name: 'Plan ahead', parent: team };
  // The commissioner's scoring tool belongs to Settings (PLAN-2026-10-18 §2.5).
  if (rest === '/settings/scoring')
    return { name: 'Change scoring', parent: up('/settings', 'Settings') };
  if (rest === '/compare') return { name: 'Compare', parent: up('/players', 'Players') };
  const LEAGUE: Record<string, string> = {
    '/scoreboard': 'Scoreboard',
    '/standings': 'Standings',
    '/pickem': 'Pick’em',
    '/challenge': 'Weekly challenge',
    '/check-in': 'Check-in',
    '/worth-a-look': 'Worth a look',
    '/schedule': tab.get('tab') === 'playoffs' ? 'Playoffs' : 'Schedule',
    '/news': 'News',
    '/recap': 'Recap',
    // Another team's when it names one (`?team=`), mine otherwise.
    '/wrapped': tab.has('team') ? 'A team’s season' : 'Your season',
    '/yearbook': 'The yearbook',
    '/awards': 'Awards',
    '/history': 'History',
    '/keepers': 'Keepers',
    '/draft-recap': 'Draft recap',
    '/rules': 'Rules',
    '/managers': 'Managers',
    '/audit': 'Commissioner log',
    '/settings': 'Settings',
  };
  const name = LEAGUE[rest];
  return name === undefined ? null : { name, parent: front };
}

const LEAGUES: Way = { to: '/', label: 'Leagues' };
const ACCOUNT: Way = { to: '/account', label: 'Account' };

/** Outside a league: the hub, the account's screens, starting and joining one. */
const HUB: Record<string, Screen> = {
  '/': { name: 'Leagues', parent: null },
  '/demo': { name: 'Chapters', parent: null },
  '/demo/leagues': { name: 'Leagues', parent: { to: '/demo', label: 'Chapters' } },
  '/account': { name: 'Account', parent: LEAGUES },
  '/account/notifications': { name: 'Notifications', parent: ACCOUNT },
  '/account/achievements': { name: 'Achievements', parent: ACCOUNT },
  '/account/install': { name: 'Install the app', parent: ACCOUNT },
  '/leagues/new': { name: 'Start a league', parent: LEAGUES },
  '/join': { name: 'Join a league', parent: LEAGUES },
};

/**
 * What the screen at this address is called and where it belongs, or null for
 * an address this app does not draw as a screen of its own (Appearance, which
 * is a sheet; a redirect; the front door).
 */
export function screenAt(pathname: string, search = '', setup = false): Screen | null {
  const league = /^(\/l\/[^/]+)(.*)$/.exec(pathname);
  if (league) return leagueScreen(league[1]!, league[2]!, search, setup);
  if (pathname.startsWith('/join/')) return HUB['/join']!;
  return HUB[pathname] ?? null;
}

// ── The history ────────────────────────────────────────────────────────────

type Entry = { path: string; search: string; jump: boolean; name?: string };

const KEY = 'fsp.wayBack.v1';
/** The most entries kept: a long session's oldest fall off the bottom. */
const KEEP = 200;

function load(): Map<number, Entry> {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw === null) return new Map();
    return new Map(JSON.parse(raw) as [number, Entry][]);
  } catch {
    return new Map();
  }
}

const entries: Map<number, Entry> = typeof window === 'undefined' ? new Map() : load();

function save(): void {
  try {
    const kept = [...entries].sort((a, b) => a[0] - b[0]).slice(-KEEP);
    window.sessionStorage.setItem(KEY, JSON.stringify(kept));
  } catch {
    // Private mode or storage blocked: the way back still works for this
    // page's life, from memory.
  }
}

/**
 * The entry on screen, by BrowserRouter's number for it, or 0 for an entry it
 * did not number. Only a whole number: over a state with no `idx` the router
 * numbers its next entry NaN, which a Map finds again at NaN - 1, and the walks
 * back through a screen's entries below never ended.
 */
function idxNow(): number {
  const state = window.history.state as { idx?: unknown } | null;
  const idx = state?.idx;
  return typeof idx === 'number' && Number.isSafeInteger(idx) ? idx : 0;
}

/**
 * When a jump was announced: a tab, the rail, the league switcher. Heard by
 * the next entry noted within a few seconds, which allows for a league's
 * screens settling first (the frame is not drawn until they have).
 */
let jumpAt = 0;

/** The next screen change is a jump between places, not a step deeper. */
export function jumping(): void {
  jumpAt = Date.now();
}

/** Note the entry on screen. Exported for the tests, which have no router. */
export function record(
  idx: number,
  path: string,
  search: string,
  type: 'PUSH' | 'REPLACE' | 'POP',
): void {
  const jumped = Date.now() - jumpAt < 5000;
  jumpAt = 0;
  if (type === 'PUSH') {
    // A new entry ends the history after it, as the browser's does.
    for (const k of [...entries.keys()]) if (k > idx) entries.delete(k);
  }
  const was = entries.get(idx);
  const same = was !== undefined && was.path === path;
  entries.set(idx, {
    path,
    search,
    // Back or Forward onto an entry keeps how it was first reached, and so
    // does an entry changed in place (a tool switched in the room, a
    // redirect): it is still the entry the person arrived on.
    jump:
      type === 'POP'
        ? (was?.jump ?? false)
        : jumped || (type === 'REPLACE' && (was?.jump ?? false)),
    ...(same && was.name !== undefined ? { name: was.name } : {}),
  });
  save();
}

/**
 * How many steps back the screen before this one is, landing on its first
 * entry (not a sheet's left behind on top of it), or null when the entry
 * before is not a screen of this app's.
 */
export function stepsToScreenBefore(): number | null {
  const idx = idxNow();
  const before = entries.get(idx - 1);
  if (before === undefined || screenAt(before.path, before.search) === null) return null;
  let first = idx - 1;
  while (entries.get(first - 1)?.path === before.path) first--;
  return idx - first;
}

/** The screen before this one, past this one's own entries, was at `path`. */
export function cameFrom(path: string): boolean {
  const idx = idxNow();
  const here = entries.get(idx);
  if (here === undefined) return false;
  let first = idx;
  while (entries.get(first - 1)?.path === here.path) first--;
  const before = entries.get(first - 1);
  return before !== undefined && before.path === path && !entries.get(first)!.jump;
}

/** Forget everything: the tests' fresh start. */
export function forgetHistory(): void {
  entries.clear();
  save();
}

/**
 * Where Back goes from the entry `idx`: the screen before it, `steps` back,
 * or up to `parent` when there is none to go back to.
 */
export function wayBackFrom(
  idx: number,
  setup = false,
): { steps: number; label: string } | { up: Way } | null {
  if (!Number.isSafeInteger(idx)) return null;
  const here = entries.get(idx);
  if (here === undefined) return null;
  const screen = screenAt(here.path, here.search, setup);
  if (screen === null || screen.parent === null) return null;
  // The first entry at this address is how the screen was reached.
  let first = idx;
  while (entries.get(first - 1)?.path === here.path) first--;
  const arrival = entries.get(first)!;
  const before = entries.get(first - 1);
  if (!arrival.jump && before !== undefined) {
    const was = screenAt(before.path, before.search, setup);
    if (was !== null) return { steps: idx - (first - 1), label: before.name ?? was.name };
  }
  return { up: screen.parent };
}

/** The last entry noted, so a frame drawn again over the same one (a league
 * settling under `Booting`) does not note it twice and lose how it came. */
let noted: string | null = null;

/** Names given before their entry was noted (a screen's effects run before the frame's). */
const named = new Map<string, string>();

/** Name every entry of the screen on view, from its first. */
function nameRun(path: string, name: string): void {
  const idx = idxNow();
  if (entries.get(idx)?.path !== path) return;
  let first = idx;
  while (entries.get(first - 1)?.path === path) first--;
  for (let i = first; i <= idx; i++) {
    const e = entries.get(i);
    if (e !== undefined) entries.set(i, { ...e, name });
  }
  save();
}

/**
 * A screen's own name for itself, where its address does not say it (a team's
 * page), so the Back from the screen after it can say where it goes.
 */
export function useScreenName(name: string | null): void {
  const location = useLocation();
  useLayoutEffect(() => {
    if (name === null) return;
    named.set(location.key, name);
    nameRun(location.pathname, name);
  }, [name, location.key]);
}

/**
 * The top bar's Back for the screen on view, or null on a place's first. Notes
 * each entry as it arrives, before the screen is painted, then reads the way
 * from the record.
 */
export function useWayBack(setup: boolean): { label: string; go: () => void } | null {
  const location = useLocation();
  const type = useNavigationType();
  const navigate = useNavigate();
  const [way, setWay] = useState<ReturnType<typeof wayBackFrom>>(null);
  useLayoutEffect(() => {
    if (noted === location.key) return;
    noted = location.key;
    record(idxNow(), location.pathname, location.search, type);
    const name = named.get(location.key);
    if (name !== undefined) nameRun(location.pathname, name);
  }, [location.key]);
  useLayoutEffect(() => {
    setWay(wayBackFrom(idxNow(), setup));
  }, [location.key, setup]);
  return useMemo(() => {
    if (way === null) return null;
    // BACK MOVES BACKWARD, in the look's own way (B-404).
    if ('steps' in way)
      return {
        label: way.label,
        go: () => morph(() => navigate(-way.steps), { motion: 'back' }),
      };
    return {
      label: way.up.label,
      go: () => {
        // Up is a jump of its own (its Back goes up again, never back down
        // to here), and moves as Back does.
        jumping();
        tabMoves('back');
        morph(() => navigate(way.up.to), { motion: 'back' });
      },
    };
  }, [way, navigate]);
}
